
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import moment from 'moment';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import { COL, getTableName, sqlExtractYear, sqlExtractMonth, sqlSumByDocType, sqlSum } from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup, ValueText } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		SecondaryLineBarChartFilterable,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class PurchaseTrendBarLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['purchases', 'suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public xAxisData: string[] = [];
	public periodTotalData: any[] = [];
	public series: echarts.ECharts[] | echarts.ECharts | any = [];

	public selectedColumn: string = this.columnList[0].value;
	public selectedLine: string = this.lineList[0].value;
	public selectedComparedYear: number | null = this.currentYear - 1;
    public selectedTimeframe: string = 'Monthly';

	public columnDatas: number[] = [];
	public comparedColumnDatas: number[] = [];
	public accumulatedLineResults: number[] = [];
	public accumulatedComparedLineResults: number[] = [];

	// Cached raw data
	private _cachedData: {
		// PH mode data
		phPurchaseAmounts: number[];
		phCnAmounts: number[];
		phPurchaseAmountsLY: number[];
		phCnAmountsLY: number[];
		// AP mode data
		apPurchaseAmounts: number[];
		apSdAmounts: number[];
		apCnAmounts: number[];
		apPurchaseAmountsLY: number[];
		apSdAmountsLY: number[];
		apCnAmountsLY: number[];
		// Payment
		paymentAmounts: number[];
		paymentAmountsLY: number[];
		// Outstanding
		pisdAmounts: number[];
		koAmounts: number[];
		gainLossAmounts: number[];
		unappliedAmounts: number[];
		pisdAmountsLY: number[];
		koAmountsLY: number[];
		gainLossAmountsLY: number[];
		unappliedAmountsLY: number[];
	} | null = null;
	private _cachedDataKey: string = '';

	private get _dataFetchKey(): string {
		return JSON.stringify([
			this.computedYearRange,
			this.compareYearRange,
			this.selectedSuppliers,
		]);
	}

	/**
	 * Applies 3-month or 6-month grouping to columnDatas and comparedColumnDatas.
	 * Assumes input arrays are 12 monthly values in oldest-first order (index 0 = oldest month).
	 * Output will also be in oldest-first order.
	 * @param monthLabels - Array of 12 month labels (oldest to newest)
	 */
	public applyTimeframeGrouping(monthLabels: Array<{ text: string }>) {
		if (this.selectedTimeframe === '3 months') {
			// Group 12 months into 4 quarters (oldest first)
			// Input: [Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec, Jan] (oldest first)
			// Output: [Feb-Apr, May-Jul, Aug-Oct, Nov-Jan] (oldest first)
			const groupedData: number[] = [];
			const groupedComparedData: number[] = [];

			for (let i = 0; i < 4; i++) {
				const startIdx = i * 3;
				groupedData.push(
					this.columnDatas[startIdx] + this.columnDatas[startIdx + 1] + this.columnDatas[startIdx + 2],
				);
				groupedComparedData.push(
					this.comparedColumnDatas[startIdx]
					+ this.comparedColumnDatas[startIdx + 1]
					+ this.comparedColumnDatas[startIdx + 2],
				);
			}

			this.columnDatas = groupedData;
			this.comparedColumnDatas = groupedComparedData;

			// Labels for oldest-first order: [0-2], [3-5], [6-8], [9-11]
			this.xAxisData = [
				`${monthLabels[0].text} - ${monthLabels[2].text}`,
				`${monthLabels[3].text} - ${monthLabels[5].text}`,
				`${monthLabels[6].text} - ${monthLabels[8].text}`,
				`${monthLabels[9].text} - ${monthLabels[11].text}`,
			];
		} else if (this.selectedTimeframe === '6 months') {
			// Group 12 months into 2 halves (oldest first)
			const groupedData: number[] = [];
			const groupedComparedData: number[] = [];

			// First half: oldest 6 months (indices 0-5)
			groupedData.push(
				this.columnDatas[0] + this.columnDatas[1] + this.columnDatas[2] +
				this.columnDatas[3] + this.columnDatas[4] + this.columnDatas[5],
			);
			groupedComparedData.push(
				this.comparedColumnDatas[0] + this.comparedColumnDatas[1] + this.comparedColumnDatas[2] +
				this.comparedColumnDatas[3] + this.comparedColumnDatas[4] + this.comparedColumnDatas[5],
			);

			// Second half: newest 6 months (indices 6-11)
			groupedData.push(
				this.columnDatas[6] + this.columnDatas[7] + this.columnDatas[8] +
				this.columnDatas[9] + this.columnDatas[10] + this.columnDatas[11],
			);
			groupedComparedData.push(
				this.comparedColumnDatas[6] + this.comparedColumnDatas[7] + this.comparedColumnDatas[8] +
				this.comparedColumnDatas[9] + this.comparedColumnDatas[10] + this.comparedColumnDatas[11],
			);

			this.columnDatas = groupedData;
			this.comparedColumnDatas = groupedComparedData;

			// Labels for oldest-first order: [0-5], [6-11]
			this.xAxisData = [
				`${monthLabels[0].text} - ${monthLabels[5].text}`,
				`${monthLabels[6].text} - ${monthLabels[11].text}`,
			];
		}
	}

	public get yAxisLabel2() {
		let label: string = '';

		// if (this.selectedLine === 'salesMargin') {
		// 	label = '%';
		// }

		if (
			this.selectedLine === 'cummulativePurchases' ||
			this.selectedLine === 'cummulativeCreditNote' ||
			this.selectedLine === 'outstanding'
		) {
			label = this.currencySymbol;
		}
		return label;
	}

	public get compareYearList() {
		const currentYear = moment().year();

		const list: number[] = [];

		for (let i = 1; i < 30; i++) {
			const year = currentYear - i;
			list.push(year);
		}

		return list;
	}

	public get timeframe() {
		const timeframeList = [
			'Monthly',
			'3 months',
			'6 months',
		];
		return timeframeList;
	}

	public get selectedLineName() {
		return this.lineList.find((item) => item.value === this.selectedLine)!.text;
	}

	public get columnList(): any[] {
		const list = [
			{ text: 'Purchase', value: 'purchase' },
			{ text: 'Credit Note', value: 'creditNote' },
			{ text: 'Payment', value: 'payment' },
		];

		return list;
	}

	public get lineList(): ValueText[] {
		const list = [
			{ text: 'None', value: 'none' },
			{ text: 'Outstanding', value: 'outstanding' },
			// { text: 'Sales Margin', value: 'salesMargin' },
			{ text: 'Cummulative Purchases', value: 'cummulativePurchases' },
			{ text: 'Cummulative Credit Note', value: 'cummulativeCreditNote' },
		];

		return list;
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currentMonth() {
		return moment(this.selectedAsOfDate).month();
	}

	/**
	 * 12 months ending at the selected date range end date.
	 * Example: If selectedDateRange ends in Oct 2024, returns Nov 2023 - Oct 2024.
	 */
	public get computedYearRange(): [number, number] {
		return [
			moment(this.selectedDateRange[1])
				.add(-1, 'year')
				.add(1, 'month')
				.startOf('month').valueOf(),
			moment(this.selectedDateRange[1]).valueOf(),
		];
	}

	/**
	 * Same 12-month period as computedYearRange, but 1 year earlier.
	 * Example: If computedYearRange is Nov 2023 - Oct 2024, returns Nov 2022 - Oct 2023.
	 */
	public get compareYearRange(): [number, number] {
		return [
			moment(this.computedYearRange[0])
				.add(-1, 'year')
				.valueOf(),
			moment(this.computedYearRange[1])
				.add(-1, 'year')
				.endOf('month')
				.valueOf(),
		];
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.salesTracker;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedSuppliers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedColumn,
			selectedTimeframe,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedSuppliers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedColumn,
			selectedTimeframe,
		]);
	}

	public async expensiveCalc() {
		const jobId = (this.currentJobId = uuid.v4());
		this.$forceUpdate();
		this.generateLoadingText(0.2, 0.3);

		const currentFetchKey = this._dataFetchKey;
		const useCache = this._cachedData !== null && this._cachedDataKey === currentFetchKey;

		let phPurchaseAmounts: number[];
		let phCnAmounts: number[];
		let phPurchaseAmountsLY: number[];
		let phCnAmountsLY: number[];
		let apPurchaseAmounts: number[];
		let apSdAmounts: number[];
		let apCnAmounts: number[];
		let apPurchaseAmountsLY: number[];
		let apSdAmountsLY: number[];
		let apCnAmountsLY: number[];
		let paymentAmounts: number[];
		let paymentAmountsLY: number[];
		let pisdAmounts: number[];
		let koAmounts: number[];
		let gainLossAmounts: number[];
		let unappliedAmounts: number[];
		let pisdAmountsLY: number[];
		let koAmountsLY: number[];
		let gainLossAmountsLY: number[];
		let unappliedAmountsLY: number[];

		if (useCache) {
			phPurchaseAmounts = this._cachedData!.phPurchaseAmounts;
			phCnAmounts = this._cachedData!.phCnAmounts;
			phPurchaseAmountsLY = this._cachedData!.phPurchaseAmountsLY;
			phCnAmountsLY = this._cachedData!.phCnAmountsLY;
			apPurchaseAmounts = this._cachedData!.apPurchaseAmounts;
			apSdAmounts = this._cachedData!.apSdAmounts;
			apCnAmounts = this._cachedData!.apCnAmounts;
			apPurchaseAmountsLY = this._cachedData!.apPurchaseAmountsLY;
			apSdAmountsLY = this._cachedData!.apSdAmountsLY;
			apCnAmountsLY = this._cachedData!.apCnAmountsLY;
			paymentAmounts = this._cachedData!.paymentAmounts;
			paymentAmountsLY = this._cachedData!.paymentAmountsLY;
			pisdAmounts = this._cachedData!.pisdAmounts;
			koAmounts = this._cachedData!.koAmounts;
			gainLossAmounts = this._cachedData!.gainLossAmounts;
			unappliedAmounts = this._cachedData!.unappliedAmounts;
			pisdAmountsLY = this._cachedData!.pisdAmountsLY;
			koAmountsLY = this._cachedData!.koAmountsLY;
			gainLossAmountsLY = this._cachedData!.gainLossAmountsLY;
			unappliedAmountsLY = this._cachedData!.unappliedAmountsLY;
		} else {
			const queryHead = await this._ensureTablesLoaded(
				['purchases', 'purchaseReturn', 'supplierTrades', 'supplierPiSds', 'supplierKos'] as any[],
			);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.3, 0.9);

			const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
			const purchasesTable = getTableName('purchases');
			const returnTable = getTableName('purchaseReturn');
			const tradesTable = getTableName('supplierTrades');
			const pisdTable = getTableName('supplierPiSds');
			const koTable = getTableName('supplierKos');

			let supplierFilter = '';
			if (this.selectedSuppliers && this.selectedSuppliers.length > 0) {
				const list = this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(',');
				supplierFilter = ` AND "CODE" IN (${list})`;
			}

			const buildDateWhere = (range: [number, number]) =>
				`TRY_CAST("DOCDATE" AS BIGINT) >= ${range[0]} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${range[1]}`;

			const buildKoDateWhere = (range: [number, number]) =>
				`TRY_CAST("FROMDOCDATE" AS BIGINT) >= ${range[0]} AND TRY_CAST("FROMDOCDATE" AS BIGINT) <= ${range[1]}`;

			// purchases: PI+CP amounts and SD amounts by year/month
			const loadPurchaseData = (range: [number, number]) =>
				queryHead.rawQuery(`
					SELECT ${sqlExtractYear(COL.DOCDATE)},
						${sqlExtractMonth(COL.DOCDATE)},
						${sqlSumByDocType(['PI', 'CP'], COL.AMOUNT, 'pi_cp_amount')},
						${sqlSumByDocType(['SD'], COL.AMOUNT, 'sd_amount')}
					FROM ${purchasesTable}
					WHERE ${buildDateWhere(range)}${supplierFilter}
					GROUP BY year, month
					ORDER BY year, month
				`);

			// purchaseReturn: CN amounts by year/month
			const loadReturnData = (range: [number, number]) =>
				queryHead.rawQuery(`
					SELECT ${sqlExtractYear(COL.DOCDATE)},
						${sqlExtractMonth(COL.DOCDATE)},
						${sqlSum(COL.AMOUNT, 'amount')}
					FROM ${returnTable}
					WHERE ${buildDateWhere(range)}${supplierFilter}
					GROUP BY year, month
					ORDER BY year, month
				`);

			// supplierTrades: PI/SD/SC amounts + SP payment + unapplied by year/month
			const loadTradesData = (range: [number, number]) =>
				queryHead.rawQuery(`
					SELECT ${sqlExtractYear(COL.DOCDATE)},
						${sqlExtractMonth(COL.DOCDATE)},
						${sqlSumByDocType(['PI'], COL.LOCALAMOUNT, 'pi_amount')},
						${sqlSumByDocType(['SD'], COL.LOCALAMOUNT, 'sd_amount')},
						${sqlSumByDocType(['SC'], COL.LOCALAMOUNT, 'sc_amount')},
						${sqlSumByDocType(['SP'], COL.LOCALAMOUNT, 'sp_amount')},
						${sqlSum('"UNAPPLIEDAMT"', 'unapplied_amount')}
					FROM ${tradesTable}
					WHERE ${buildDateWhere(range)}${supplierFilter}
					GROUP BY year, month
					ORDER BY year, month
				`);

			// supplierPiSds: LOCALDOCAMT by year/month
			const loadPiSdData = (range: [number, number]) =>
				queryHead.rawQuery(`
					SELECT ${sqlExtractYear(COL.DOCDATE)},
						${sqlExtractMonth(COL.DOCDATE)},
						${sqlSum('"LOCALDOCAMT"', 'amount')}
					FROM ${pisdTable}
					WHERE ${buildDateWhere(range)}${supplierFilter}
					GROUP BY year, month
					ORDER BY year, month
				`);

			// supplierKos: KOLOCALAMT + KOGAINLOSS by year/month (grouped by FROMDOCDATE)
			const loadKoData = (range: [number, number]) =>
				queryHead.rawQuery(`
					SELECT ${sqlExtractYear('"FROMDOCDATE"')},
						${sqlExtractMonth('"FROMDOCDATE"')},
						${sqlSum('"KOLOCALAMT"', 'amount')},
						${sqlSum('"KOGAINLOSS"', 'gain_loss')}
					FROM ${koTable}
					WHERE ${buildKoDateWhere(range)}${supplierFilter}
					GROUP BY year, month
					ORDER BY year, month
				`);

			const [
				phDataTY, phReturnTY, tradesDataTY, pisdDataTY, koDataTY,
				phDataLY, phReturnLY, tradesDataLY, pisdDataLY, koDataLY,
			] = await Promise.all([
				loadPurchaseData(this.computedYearRange),
				loadReturnData(this.computedYearRange),
				loadTradesData(this.computedYearRange),
				loadPiSdData(this.computedYearRange),
				loadKoData(this.computedYearRange),
				loadPurchaseData(this.compareYearRange),
				loadReturnData(this.compareYearRange),
				loadTradesData(this.compareYearRange),
				loadPiSdData(this.compareYearRange),
				loadKoData(this.compareYearRange),
			]);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.95);

			const align = <T>(
				data: Array<{ year: number; month: number } & T>,
				range: [number, number],
				fn: (i: T) => number,
			) => this._alignToMonthArray(data, range, fn);

			const mapRows = (rows: any[]) => rows.map((r) => ({
				year: Number(r.year), month: Number(r.month),
				pi_cp_amount: Number(r.pi_cp_amount) || 0,
				sd_amount: Number(r.sd_amount) || 0,
				amount: Number(r.amount) || 0,
				pi_amount: Number(r.pi_amount) || 0,
				sc_amount: Number(r.sc_amount) || 0,
				sp_amount: Number(r.sp_amount) || 0,
				unapplied_amount: Number(r.unapplied_amount) || 0,
				gain_loss: Number(r.gain_loss) || 0,
			}));

			const phTY = mapRows(phDataTY);
			const phRetTY = mapRows(phReturnTY);
			const trTY = mapRows(tradesDataTY);
			const pisTY = mapRows(pisdDataTY);
			const koTY = mapRows(koDataTY);
			const phLY = mapRows(phDataLY);
			const phRetLY = mapRows(phReturnLY);
			const trLY = mapRows(tradesDataLY);
			const pisLY = mapRows(pisdDataLY);
			const koLY = mapRows(koDataLY);

			// PH mode
			phPurchaseAmounts = align(phTY, this.computedYearRange,
				(i) => i.pi_cp_amount + (this.includeDebitNote ? i.sd_amount : 0));
			phCnAmounts = align(phRetTY, this.computedYearRange, (i) => i.amount);
			phPurchaseAmountsLY = align(phLY, this.compareYearRange,
				(i) => i.pi_cp_amount + (this.includeDebitNote ? i.sd_amount : 0));
			phCnAmountsLY = align(phRetLY, this.compareYearRange, (i) => i.amount);

			// AP mode
			apPurchaseAmounts = align(trTY, this.computedYearRange,
				(i) => i.pi_amount + (this.includeDebitNote ? i.sd_amount : 0));
			apSdAmounts = align(trTY, this.computedYearRange, (i) => i.sd_amount);
			apCnAmounts = align(trTY, this.computedYearRange, (i) => i.sc_amount);
			apPurchaseAmountsLY = align(trLY, this.compareYearRange,
				(i) => i.pi_amount + (this.includeDebitNote ? i.sd_amount : 0));
			apSdAmountsLY = align(trLY, this.compareYearRange, (i) => i.sd_amount);
			apCnAmountsLY = align(trLY, this.compareYearRange, (i) => i.sc_amount);

			// Payment (from supplierTrades SP)
			paymentAmounts = align(trTY, this.computedYearRange, (i) => i.sp_amount);
			paymentAmountsLY = align(trLY, this.compareYearRange, (i) => i.sp_amount);

			// Outstanding components
			pisdAmounts = align(pisTY, this.computedYearRange, (i) => i.amount);
			koAmounts = align(koTY, this.computedYearRange, (i) => i.amount);
			gainLossAmounts = align(koTY, this.computedYearRange, (i) => i.gain_loss);
			unappliedAmounts = align(trTY, this.computedYearRange, (i) => i.unapplied_amount);
			pisdAmountsLY = align(pisLY, this.compareYearRange, (i) => i.amount);
			koAmountsLY = align(koLY, this.compareYearRange, (i) => i.amount);
			gainLossAmountsLY = align(koLY, this.compareYearRange, (i) => i.gain_loss);
			unappliedAmountsLY = align(trLY, this.compareYearRange, (i) => i.unapplied_amount);

			this._cachedData = {
				phPurchaseAmounts, phCnAmounts, phPurchaseAmountsLY, phCnAmountsLY,
				apPurchaseAmounts, apSdAmounts, apCnAmounts,
				apPurchaseAmountsLY, apSdAmountsLY, apCnAmountsLY,
				paymentAmounts, paymentAmountsLY,
				pisdAmounts, koAmounts, gainLossAmounts, unappliedAmounts,
				pisdAmountsLY, koAmountsLY, gainLossAmountsLY, unappliedAmountsLY,
			};
			this._cachedDataKey = currentFetchKey;
		}

		// Select based on PH/AP mode
		const purchaseAmts = this.slPhModuleOnly ? phPurchaseAmounts : apPurchaseAmounts;
		const cnAmts = this.slPhModuleOnly ? phCnAmounts : apCnAmounts;
		const purchaseAmtsLY = this.slPhModuleOnly ? phPurchaseAmountsLY : apPurchaseAmountsLY;
		const cnAmtsLY = this.slPhModuleOnly ? phCnAmountsLY : apCnAmountsLY;

		let lineDatas: number[] = [];
		let comparedLineDatas: number[] = [];

		switch (this.selectedColumn) {
			case 'purchase':
				this.columnDatas = purchaseAmts.map((v, i) => v - (this.includeCreditNote ? cnAmts[i] : 0));
				this.comparedColumnDatas = purchaseAmtsLY.map((v, i) => v - (this.includeCreditNote ? cnAmtsLY[i] : 0));
				break;
			case 'creditNote':
				this.columnDatas = [...cnAmts];
				this.comparedColumnDatas = [...cnAmtsLY];
				break;
			case 'payment':
				this.columnDatas = [...paymentAmounts];
				this.comparedColumnDatas = [...paymentAmountsLY];
				break;
		}

		switch (this.selectedLine) {
			case 'outstanding':
				lineDatas = pisdAmounts.map((v, i) => v - koAmounts[i] - gainLossAmounts[i] - unappliedAmounts[i]);
				comparedLineDatas = pisdAmountsLY.map((v, i) => v - koAmountsLY[i] - gainLossAmountsLY[i] - unappliedAmountsLY[i]);
				break;
			case 'cummulativePurchases':
				lineDatas = purchaseAmts.map((v, i) => v - (this.includeCreditNote ? cnAmts[i] : 0));
				comparedLineDatas = purchaseAmtsLY.map((v, i) => v - (this.includeCreditNote ? cnAmtsLY[i] : 0));
				break;
			case 'none':
				break;
		}

		for (let i = 0; i < 12; i++) {
			this.accumulatedComparedLineResults[i] = i === 0
				? comparedLineDatas[i]
				: comparedLineDatas[i] + this.accumulatedComparedLineResults[i - 1];
			this.accumulatedLineResults[i] = i === 0
				? lineDatas[i]
				: lineDatas[i] + this.accumulatedLineResults[i - 1];
		}

		this.generateLoadingText(1);

		const foundColumn = this.columnList.find((c) => c.value === this.selectedColumn);
		const columnName = foundColumn ? foundColumn.text : this.selectedColumn;

		// Build x-axis labels
		const monthLabels = this._buildMonthLabels();
		this.xAxisData = monthLabels;

		// Apply timeframe grouping if needed
		if (this.selectedTimeframe !== 'Monthly') {
			this.applyTimeframeGrouping(monthLabels.map((t) => ({ text: t })));
		}

		const yearDatas: any = {
			name: columnName + ' This Year', type: 'bar', barWidth: '30%',
			itemStyle: { color: GobiColor.COLOR1 }, data: this.columnDatas,
		};
		const comparedYearDatas: any = {
			name: columnName + ' Last Year', type: 'bar', barWidth: '30%',
			itemStyle: { color: GobiColor.COLOR3 }, data: this.comparedColumnDatas,
		};
		const line: any = {
			name: this.selectedLineName + ' This Year', type: 'line',
			itemStyle: { color: GobiColor.COLOR6 }, yAxisIndex: 1, data: this.accumulatedLineResults,
		};
		const comparedLine: any = {
			name: this.selectedLineName + ' Last Year', type: 'line',
			itemStyle: { color: GobiColor.COLOR7 }, yAxisIndex: 1, data: this.accumulatedComparedLineResults,
		};

		if (this.selectedTimeframe === 'Monthly' && this.selectedLine !== 'none') {
			this.series = [yearDatas, comparedYearDatas, line, comparedLine];
		} else {
			this.series = [yearDatas, comparedYearDatas];
		}

		this.saveHistory('series', 'xAxisData', 'columnDatas', 'comparedColumnDatas',
			'accumulatedLineResults', 'accumulatedComparedLineResults');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}

	private _buildMonthLabels(): string[] {
		const labels: string[] = [];
		const start = moment(this.computedYearRange[0]);
		for (let i = 0; i < 12; i++) {
			labels.push(start.clone().add(i, 'month').format('MMM YY'));
		}
		return labels;
	}

	private _alignToMonthArray<T>(
		data: Array<{ year: number; month?: number } & T>,
		dateRange: [number, number],
		getValue: (item: T) => number,
	): number[] {
		const result = Array(12).fill(0);
		const startMonth = moment(dateRange[0]).month() + 1;
		const startYear = moment(dateRange[0]).year();
		for (const item of data) {
			const month = item.month || 1;
			const pos = (item.year - startYear) * 12 + (month - startMonth);
			if (pos >= 0 && pos < 12) {
				result[pos] = getValue(item as any);
			}
		}
		return result;
	}
}
